import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { plural } from '../../lib/plural';
import { LANG_LABEL } from '../../lib/siddhiText';
import { Loader2, RefreshCw, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const CHOICE = { resonates: 'resonates', saveForLater: 'saved for later', notForMe: 'not for me' };

const Stat = ({ label, value, testId }) => (
  <div className="admin-stat-card">
    <p className="admin-stat-label">{label}</p>
    <p className="admin-stat-value" data-testid={testId}>{value}</p>
  </div>
);

/* Same PDF the user downloads from My Journey — admin-only, read-only. */
export const AdminJourneyPdfButton = ({ userId, token, testId }) => {
  const [state, setState] = useState('idle');
  const download = async (e) => {
    e.stopPropagation();
    setState('busy');
    try {
      const r = await axios.get(`${API}/api/admin/users/${userId}/journey.pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement('a'), { href: url, download: `journey-${userId}.pdf` }).click();
      URL.revokeObjectURL(url);
      setState('idle');
    } catch (err) {
      setState('error');
    }
  };
  return (
    <button type="button" className="admin-action-btn admin-export-btn" onClick={download} disabled={state === 'busy'} data-testid={testId}>
      <Download size={14} /> {state === 'busy' ? 'Preparing…' : state === 'error' ? 'PDF failed — retry' : 'Download journey (PDF)'}
    </button>
  );
};

const ActivityDetail = ({ user, token }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios
      .get(`${API}/api/admin/users/${user.id}/activity`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => setData({ whispers: [], chapters: [], error: true }));
  }, [user.id, token]);

  if (!data) return <Loader2 className="admin-spinner" size={16} />;
  const { books } = user.sadhana;
  return (
    <div className="admin-user-detail" data-testid={`admin-user-detail-${user.id}`}>
      <div className="admin-detail-actions">
        <Link to={`/admin/users/${user.id}/view`} className="admin-action-btn admin-view-btn" data-testid={`admin-user-detail-view-${user.id}`}>View dashboard</Link>
        <AdminJourneyPdfButton userId={user.id} token={token} testId={`admin-user-pdf-${user.id}`} />
      </div>
      <h4>Sadhana by book</h4>
      {books.length === 0 ? <p className="admin-empty">No Sadhana practice started yet.</p> : (
        <ul>
          {books.map((b) => (
            <li key={b.book_id}>
              <strong>{b.book_title}</strong> — {b.completions} completion{b.completions === 1 ? '' : 's'},
              best streak {b.best_streak}d, current {b.current_streak}d, last done {b.last_done || '—'}
            </li>
          ))}
        </ul>
      )}
      <h4>Sankalpa seals ({(data.seals || []).length})</h4>
      {(data.seals || []).length === 0 ? <p className="admin-empty">None.</p> : (
        <ul>{data.seals.map((x) => <li key={`${x.book_id}-${x.lang}`} data-testid={`admin-user-seal-${user.id}-${x.book_id}-${x.lang}`}><strong>{x.book_title}</strong> ({x.lang}) · {fmt(x.sealed_at)} — Sealed: {(x.idea_titles || x.idea_ids).join(' · ')}</li>)}</ul>
      )}
      <h4>Bodha decisions ({(data.bodha_choices || []).length})</h4>
      {(data.bodha_choices || []).length === 0 ? <p className="admin-empty">None.</p> : (
        <ul>{data.bodha_choices.map((x) => <li key={`${x.book_id}-${x.idea_id}`} data-testid={`admin-user-bodha-${user.id}-${x.book_id}-${x.idea_id}`}><strong>{x.book_title}</strong> · {x.idea_title || `Idea ${x.idea_id}`} — {CHOICE[x.choice] || x.choice} · {fmt(x.updated_at)}</li>)}</ul>
      )}
      <h4>Siddhi versions ({(data.siddhi_versions || []).length})</h4>
      {(data.siddhi_versions || []).length === 0 ? <p className="admin-empty">Not generated yet.</p> : (
        data.siddhi_versions.map((x) => (
          <div key={`${x.book_id}-${x.lang}-${x.version}`} data-testid={`admin-user-siddhi-${user.id}-${x.book_id}-v${x.version}`} style={{ marginBottom: 10 }}>
            <strong>{x.book_title}</strong> · <span data-testid={`admin-user-siddhi-label-${x.book_id}-v${x.version}`}>{x.legacy ? 'Version 1 (legacy)' : `Version ${x.version}`} · {LANG_LABEL[x.lang] || LANG_LABEL.en}</span> · {fmt(x.generated_at)}
            {x.note ? <> · note: <em>{x.note}</em></> : null}
            {x.checkins_at_generation ? <> · {plural(x.checkins_at_generation.days_kept, 'day')} kept, longest run {x.checkins_at_generation.longest_run}, {plural(x.checkins_at_generation.misses, 'miss')} ({x.checkins_at_generation.recoveries} recovered)</> : null}
            <ul>
              <li><em>Wish:</em> {x.woop?.wish}</li>
              <li><em>Outcome:</em> {x.woop?.outcome}</li>
              <li><em>Obstacle:</em> {x.woop?.obstacle}</li>
              <li><em>Plan:</em> {x.woop?.plan}</li>
              <li><em>Identity:</em> {x.identity_statement}</li>
              {x.woop?.arrived ? <li><em>What arrived:</em> {x.woop.arrived}</li> : null}
              {x.woop?.in_the_way ? <li><em>Vighna:</em> {x.woop.in_the_way}</li> : null}
              {(x.identity_edits || []).map((e) => <li key={e.at}><em>Identity edited {fmt(e.at)}:</em> {e.text}</li>)}
              {(x.reflections_at_generation || []).map((r) => (
                <li key={r.idea_id}><em>At generation — {r.title}:</em> Phala “{r.phala}” · Vighna “{r.vighna}” · Upaya “{r.upaya || '—'}”</li>
              ))}
            </ul>
          </div>
        ))
      )}
      <h4>Book Whispers ({data.whispers.length})</h4>
      {data.whispers.length === 0 ? <p className="admin-empty">None.</p> : (
        <ul>{data.whispers.map((w) => <li key={w.id}><strong>{w.book_title || 'Untitled'}</strong> · {fmt(w.timestamp)} — {w.reflection}</li>)}</ul>
      )}
      <h4>Chapters Within ({data.chapters.length})</h4>
      {data.chapters.length === 0 ? <p className="admin-empty">None.</p> : (
        <ul>{data.chapters.map((c) => <li key={c.id}><strong>{c.book_title}</strong> · {fmt(c.timestamp)} — {c.reflection}</li>)}</ul>
      )}
    </div>
  );
};

const UserRow = ({ u, idx, token }) => {
  const [open, setOpen] = useState(false);
  const s = u.sadhana;
  return (
    <>
      <tr data-testid={`admin-user-row-${u.id}`} onClick={() => setOpen(!open)} style={{ cursor: 'pointer' }}>
        <td className="admin-td-num">{open ? <ChevronDown size={12} /> : <ChevronRight size={12} />} {idx + 1}</td>
        <td className="admin-td-email">
          <Link to={`/admin/users/${u.id}`} onClick={(e) => e.stopPropagation()} data-testid={`admin-user-open-${u.id}`}>{u.email}</Link>
          {u.is_test ? <span className="admin-tab-count"> test</span> : null}
        </td>
        <td>{u.name || '—'}</td>
        <td className="admin-td-date">{fmt(u.created_at)}</td>
        <td data-testid={`admin-user-verified-${u.id}`}>{u.email_verified ? '✓' : '✗'}</td>
        <td data-testid={`admin-user-books-${u.id}`}>{u.sealed_books_count ?? s.books.length}</td>
        <td data-testid={`admin-user-completions-${u.id}`}>{s.total_completions}</td>
        <td data-testid={`admin-user-consistency-${u.id}`}>{s.available_days ? `${s.consistency_percent}% (${s.kept_in_window}/${s.available_days})` : '—'}</td>
        <td data-testid={`admin-user-streak-${u.id}`}>{s.current_streak}d/{s.best_streak}d</td>
        <td>{s.last_done || '—'}</td>
        <td data-testid={`admin-user-stage-${u.id}`}>{u.furthest_stage || '—'}</td>
        <td>{u.last_active ? fmt(u.last_active) : '—'}</td>
        <td data-testid={`admin-user-siddhi-count-${u.id}`} className="admin-td-siddhi">
          <span className={`admin-dot${u.siddhi_count ? ' is-on' : ''}`} aria-hidden="true" />
          {u.siddhi_count ? 'Y' : 'N'} · {Math.max(u.siddhi_versions_count || 0, u.siddhi_count || 0)}
        </td>
        <td>{u.whispers_count}</td>
        <td>{u.chapters_count}</td>
        <td data-testid={`admin-user-legacy-${u.id}`}>{u.legacy_installs_count ?? u.v1_installed_count}{u.v1_only ? ' (V1 only)' : ''}</td>
        <td>
          <Link to={`/admin/users/${u.id}/view`} onClick={(e) => e.stopPropagation()} className="admin-action-btn admin-view-btn" data-testid={`admin-user-view-dashboard-${u.id}`}>View</Link>
        </td>
      </tr>
      {open ? (
        <tr><td colSpan={17} className="admin-detail-cell"><div className="admin-detail-sticky"><ActivityDetail user={u} token={token} /></div></td></tr>
      ) : null}
    </>
  );
};

const SORTS = {
  created: (u) => u.created_at || '',
  email: (u) => u.email || '',
  books: (u) => u.sealed_books_count || 0,
  stage: (u) => ({ bodha: 1, sankalpa: 2, sadhana: 3, siddhi: 4 }[u.furthest_stage] || 0),
  consistency: (u) => u.sadhana.consistency_percent || 0,
  streak: (u) => u.sadhana.current_streak || 0,
  siddhi: (u) => u.siddhi_count || 0,
  active: (u) => u.last_active || '',
  legacy: (u) => u.legacy_installs_count || 0,
};

export const AdminUsersPanel = ({ token }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [hideTest, setHideTest] = useState(false);
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState('');
  const [sortKey, setSortKey] = useState('created');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(async () => {
    setError('');
    setData(null);
    try {
      const r = await axios.get(`${API}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load users');
      setData({ summary: {}, users: [] });
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="admin-table-loading"><Loader2 className="admin-spinner" size={24} /></div>;
  const { summary, users } = data;
  const q = search.trim().toLowerCase();
  const filtered = users.filter((u) => (!hideTest || !u.is_test)
    && (!q || `${u.email} ${u.name || ''}`.toLowerCase().includes(q))
    && (!stage || (u.furthest_stage || 'none') === stage));
  const shown = [...filtered].sort((a, b) => {
    const va = SORTS[sortKey](a), vb = SORTS[sortKey](b);
    return (va > vb ? 1 : va < vb ? -1 : 0) * (sortDir === 'asc' ? 1 : -1);
  });
  const th = (key, label) => (
    <th onClick={() => { setSortDir(sortKey === key && sortDir === 'desc' ? 'asc' : 'desc'); setSortKey(key); }}
      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} data-testid={`admin-users-sort-${key}`}>
      {label}{sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
  const exportCsv = () => {
    const cols = ['email', 'name', 'signed_up', 'verified', 'books', 'furthest_stage', 'consistency_30d', 'current_streak', 'best_streak', 'siddhi_count', 'last_active', 'legacy_installs', 'completions', 'whispers', 'chapters'];
    const esc = (v) => { const t = String(v ?? ''); const safe = /^[=+\-@]/.test(t) ? `'${t}` : t; return `"${safe.replace(/"/g, '""')}"`; };
    const rows = shown.map((u) => [u.email, u.name, u.created_at, u.email_verified, u.sealed_books_count, u.furthest_stage, u.sadhana.consistency_percent, u.sadhana.current_streak, u.sadhana.best_streak, u.siddhi_count, u.last_active, u.legacy_installs_count, u.sadhana.total_completions, u.whispers_count, u.chapters_count]);
    const csv = [cols, ...rows].map((r) => r.map(esc).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    Object.assign(document.createElement('a'), { href: url, download: 'icraftz-users.csv' }).click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <section className="admin-funnel" data-testid="admin-users-funnel">
        {['bodha', 'sankalpa', 'sadhana', 'siddhi'].map((st, i) => (
          <React.Fragment key={st}>
            {i > 0 && <span className="admin-funnel-arrow" aria-hidden="true">→</span>}
            <div className="admin-funnel-step"><p className="admin-stat-label">{st[0].toUpperCase() + st.slice(1)}</p><p className="admin-stat-value" data-testid={`admin-funnel-${st}`}>{summary.funnel?.[st] ?? 0}</p></div>
          </React.Fragment>
        ))}
        <div className="admin-funnel-step admin-funnel-active"><p className="admin-stat-label">Active in last 7 days</p><p className="admin-stat-value" data-testid="admin-funnel-active-7d">{summary.active_7d ?? 0}</p></div>
        <div className="admin-funnel-step admin-funnel-active"><p className="admin-stat-label">Active in last 30 days</p><p className="admin-stat-value" data-testid="admin-funnel-active-30d">{summary.active_30d ?? 0}</p></div>
      </section>
      <section className="admin-stats" data-testid="admin-users-stats">
        <Stat label="Registered users" value={summary.total_users ?? 0} testId="admin-users-total" />
        <Stat label="Non-test users" value={summary.real_users ?? 0} testId="admin-users-real" />
        <Stat label="Verified" value={summary.verified_users ?? 0} testId="admin-users-verified" />
        <Stat label="With Sadhana" value={summary.users_with_sadhana ?? 0} testId="admin-users-sadhana" />
        <Stat label="With Siddhi" value={summary.users_with_siddhi ?? 0} testId="admin-users-siddhi" />
        <Stat label="V1-only (non-test)" value={summary.real_v1_only_users ?? 0} testId="admin-users-v1-only" />
      </section>
      <section className="admin-table-section">
        <div className="admin-table-controls">
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={hideTest} onChange={(e) => setHideTest(e.target.checked)} data-testid="admin-users-hide-test" />
            Hide test accounts
          </label>
          <input className="admin-search-input" placeholder="Search email or name" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-users-search" />
          <select value={stage} onChange={(e) => setStage(e.target.value)} data-testid="admin-users-stage-filter">
            <option value="">All stages</option>
            <option value="none">No journey</option>
            <option value="bodha">Bodha</option>
            <option value="sankalpa">Sankalpa</option>
            <option value="sadhana">Sadhana</option>
            <option value="siddhi">Siddhi</option>
          </select>
          <button className="admin-action-btn" onClick={exportCsv} data-testid="admin-users-export-csv">
            <Download size={14} /> Export CSV
          </button>
          <button className="admin-action-btn admin-refresh-btn" onClick={load} data-testid="admin-users-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
        {error && <p className="admin-error" data-testid="admin-users-error">{error}</p>}
        {shown.length === 0 ? <p className="admin-empty">No users.</p> : (
          <div className="admin-table-wrap">
            <table className="admin-table admin-table-wide" data-testid="admin-users-table">
              <thead>
                <tr>
                  <th>#</th>{th('email', 'Email')}<th>Name</th>{th('created', 'Joined')}<th title="Email verified">Ver.</th>
                  {th('books', 'Books')}<th title="Completions">Done</th>{th('consistency', 'Cons. 30d')}{th('streak', 'Streak')}<th>Last done</th>
                  {th('stage', 'Stage')}{th('active', 'Active')}{th('siddhi', 'Siddhi versions')}<th title="Book Whispers">Wh.</th><th title="Chapters Within">Ch.</th>{th('legacy', 'Legacy')}<th>Dashboard</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((u, i) => <UserRow key={u.id} u={u} idx={i} token={token} />)}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
};
